import React, { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import {
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

import { Textarea } from '@headlessui/react';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import TimePicker from 'components/TimePicker';
import { categories } from 'utils/categories';

import {
  StepShell,
  HelpedBlock,
  FormCard,
  Field,
  Segmented,
  CharCounter,
} from '../atoms';

import facebookIcon from '../../../assets/icons/socials/facebook_icon.svg';
import instagramIcon from '../../../assets/icons/socials/instagram_icon.svg';
import linkedinIcon from '../../../assets/icons/socials/linkedin_icon.svg';
import tiktokIcon from '../../../assets/icons/socials/tiktok_icon.svg';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const SHORT_DESC_MAX = 200;
const LONG_DESC_MAX = 10000;

const todayISO = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD (local)

const combineLocal = (d, t = '00:00') => {
  return d ? new Date(`${d}T${t || '00:00'}:00`) : null;
};
const getComp = (components, type) =>
  (components || []).find((c) => c.types?.includes(type));

const parseAddressComponents = (components = []) => {
  const streetNumber = getComp(components, 'street_number')?.long_name;
  const route = getComp(components, 'route')?.long_name;

  const neighborhood =
    getComp(components, 'neighborhood')?.long_name ||
    getComp(components, 'sublocality')?.long_name ||
    getComp(components, 'sublocality_level_1')?.long_name ||
    null;

  const city =
    getComp(components, 'locality')?.long_name ||
    getComp(components, 'postal_town')?.long_name ||
    getComp(components, 'administrative_area_level_2')?.long_name ||
    null;

  const region =
    getComp(components, 'administrative_area_level_1')?.long_name || null;
  const postalCode = getComp(components, 'postal_code')?.long_name || null;

  const countryComp = getComp(components, 'country');
  const country = countryComp?.long_name || null;
  const countryCode = countryComp?.short_name || null;

  const address = [streetNumber, route].filter(Boolean).join(' ') || null;

  return {
    address,
    neighborhood,
    city,
    region,
    postalCode,
    country,
    countryCode,
  };
};

const Step1 = ({
  methods,
  selectedCategories,
  setSelectedCategories,
  selectedMicroCategories,
  setSelectedMicroCategories,
}) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = methods;

  const [eventType, setEventType] = useState(getValues('date.type') || 'fixed');
  const [availableMicroCategories, setAvailableMicroCategories] = useState([]);
  const [locationOption, setLocationOption] = useState(null);
  const [location, setLocation] = useState(getValues('location') || '');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'date.days',
  });

  const shortDescValue = watch('shortDescription') || '';
  const longDescValue = watch('longDescription') || '';

  useEffect(() => {
    setValue('date.type', eventType);

    if (eventType === 'fixed') setValue('date.days', []);
    if (eventType === 'segmented') {
      // Drop any fixed-run range so it can't leak into the preview/payload.
      setValue('date.startDate', '');
      setValue('date.endDate', '');
      if (fields.length === 0) {
        append({ date: '', openHours: '', closeHours: '' });
      }
    }
  }, [eventType, append, fields.length, setValue]);

  const handleEventTypeChange = (type) => {
    setEventType(type);
    setValue('date.type', type);
    if (type === 'fixed') setValue('date.days', []);
    if (type === 'segmented') {
      setValue('date.startDate', '');
      setValue('date.endDate', '');
    }
  };

  useEffect(() => {
    const transformed = selectedCategories.map((macro) => {
      const micros = selectedMicroCategories
        .filter((m) => m.macroId === macro)
        .map((m) => m.id);
      return { macroCategoryId: macro, microCategoryIds: micros };
    });

    setValue('categoriesAllowed', transformed, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [selectedCategories, selectedMicroCategories, setValue]);

  useEffect(() => {
    const microCategories = [];
    selectedCategories.forEach((categoryId) => {
      const category = categories.find((cat) => cat.id === categoryId);
      if (category) {
        category.micro.forEach((micro) => {
          microCategories.push({
            ...micro,
            macro: category.icon,
            macroId: categoryId,
          });
        });
      }
    });
    setAvailableMicroCategories(microCategories);
  }, [selectedCategories]);

  const addDaysISO = (iso, days) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('en-CA');
  };

  const getMinSegmentDate = (index) => {
    const days = watch('date.days') || [];
    const prevDates = days
      .slice(0, index)
      .map((d) => d?.date)
      .filter(Boolean);

    const maxPrev = prevDates.length
      ? prevDates.reduce((a, b) => (a > b ? a : b))
      : null;

    if (!maxPrev) return todayISO;

    const next = addDaysISO(maxPrev, 1);
    return next > todayISO ? next : todayISO;
  };

  const socialFields = [
    {
      key: 'facebookUrl',
      label: t('event_create.facebook'),
      prefix: 'facebook.com/',
      icon: facebookIcon,
    },
    {
      key: 'instagramUrl',
      label: t('event_create.instagram'),
      prefix: 'instagram.com/',
      icon: instagramIcon,
    },
    {
      key: 'tiktokUrl',
      label: t('event_create.tiktok'),
      prefix: 'tiktok.com/@',
      icon: tiktokIcon,
    },
    {
      key: 'linkedinUrl',
      label: t('event_create.linkedin'),
      prefix: 'linkedin.com/company/',
      icon: linkedinIcon,
    },
  ];

  return (
    <StepShell
      eyebrow={t('event_create.step1_eyebrow', 'Step 01 / Details')}
      title={t('event_create.step1_title', 'What is this event?')}
      lede={t(
        'event_create.step1_lede',
        'The basics that brands will see first when deciding whether to book a space.'
      )}
    >
      {/* Identity */}
      <HelpedBlock sectionKey="identity">
      <FormCard title={t('event_create.identity', 'Identity')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field
            label={t('event_create.event_name')}
            hint={t(
              'event_create.event_name_hint',
              '60 characters or less reads best on cards.'
            )}
            error={errors.name?.message}
          >
            <input
              {...register('name')}
              className="field-input"
              placeholder={t('event_create.name_placeholder')}
            />
          </Field>

          <Field
            label={t('event_create.location')}
            hint={t(
              'event_create.location_hint',
              "We'll geocode this to a precise address."
            )}
            error={!location ? errors.location?.message : null}
          >
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                <MagnifyingGlassIcon className="w-4 h-4" />
              </span>
              <GooglePlacesAutocomplete
                apiKey={API_KEY}
                selectProps={{
                  value: locationOption,
                  noOptionsMessage: () => t('homepage.no_options'),
                  onChange: async (place) => {
                    if (!place) {
                      setLocationOption(null);
                      setLocation('');
                      setValue('location', '');
                      setValue('locationPlaceId', '');
                      setValue('locationCoords.lat', null);
                      setValue('locationCoords.lng', null);
                      setValue('lat', null);
                      setValue('lng', null);
                      return;
                    }

                    setLocationOption(place);

                    const locationDescription = place.label;
                    const placeId = place.value?.place_id;

                    setLocation(locationDescription);
                    setValue('location', locationDescription, {
                      shouldValidate: true,
                    });

                    if (placeId)
                      setValue('locationPlaceId', placeId, {
                        shouldValidate: true,
                      });

                    try {
                      const response = await fetch(
                        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${API_KEY}`
                      );
                      const data = await response.json();
                      const result = data?.results?.[0];

                      if (result) {
                        const coords = result.geometry?.location;
                        const parsed = parseAddressComponents(
                          result.address_components || []
                        );

                        setValue(
                          'formattedAddress',
                          result.formatted_address || ''
                        );
                        setValue('address', parsed.address || '');
                        setValue('neighborhood', parsed.neighborhood || '');
                        setValue('city', parsed.city || '');
                        setValue('region', parsed.region || '');
                        setValue('postalCode', parsed.postalCode || '');
                        setValue('country', parsed.country || '');
                        setValue('countryCode', parsed.countryCode || '');

                        if (coords?.lat != null && coords?.lng != null) {
                          setValue('locationCoords.lat', coords.lat, {
                            shouldValidate: true,
                          });
                          setValue('locationCoords.lng', coords.lng, {
                            shouldValidate: true,
                          });
                          setValue('lat', coords.lat);
                          setValue('lng', coords.lng);
                        }
                      }
                    } catch (error) {
                      console.error('Geocoding error:', error);
                    }
                  },
                  placeholder:
                    location || t('event_create.location_placeholder'),
                  menuPortalTarget:
                    typeof document !== 'undefined' ? document.body : null,
                  menuPosition: 'fixed',
                  styles: {
                    control: (base) => ({
                      ...base,
                      paddingLeft: 32,
                      borderRadius: 10,
                      borderColor: '#ECE9E0',
                      boxShadow: 'none',
                      minHeight: 40,
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#B7B2A5',
                      fontSize: 13,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontSize: 13,
                      color: '#1A1A1F',
                    }),
                    input: (base) => ({
                      ...base,
                      fontSize: 13,
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: 12,
                      border: '1px solid #ECE9E0',
                      boxShadow:
                        '0 1px 2px rgba(20,18,12,.04), 0 8px 24px -10px rgba(20,18,12,.18)',
                      overflow: 'hidden',
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: 13,
                      backgroundColor: state.isFocused
                        ? '#FFFAF0'
                        : '#ffffff',
                      color: '#1A1A1F',
                      cursor: 'pointer',
                    }),
                    noOptionsMessage: (base) => ({
                      ...base,
                      fontSize: 13,
                      color: '#8A8579',
                    }),
                  },
                }}
              />
            </div>
          </Field>
        </div>

        {/* Hidden fields for structured location */}
        <input type="hidden" {...register('locationPlaceId')} />
        <input type="hidden" {...register('formattedAddress')} />
        <input type="hidden" {...register('address')} />
        <input type="hidden" {...register('neighborhood')} />
        <input type="hidden" {...register('city')} />
        <input type="hidden" {...register('region')} />
        <input type="hidden" {...register('postalCode')} />
        <input type="hidden" {...register('country')} />
        <input type="hidden" {...register('countryCode')} />
        <input type="hidden" {...register('lat')} />
        <input type="hidden" {...register('lng')} />
        <input type="hidden" {...register('locationCoords.lat')} />
        <input type="hidden" {...register('locationCoords.lng')} />
      </FormCard>
      </HelpedBlock>

      {/* When it happens */}
      <HelpedBlock sectionKey="schedule">
      <FormCard
        title={t('event_create.when_it_happens', 'When it happens')}
        action={
          <Segmented
            value={eventType}
            onChange={handleEventTypeChange}
            options={[
              {
                value: 'fixed',
                label: t(
                  'event_create.schedule_fixed_short',
                  'Continuous run'
                ),
              },
              {
                value: 'segmented',
                label: t(
                  'event_create.schedule_segmented_short',
                  'Specific days'
                ),
              },
            ]}
          />
        }
      >
        {eventType === 'fixed' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Field
              label={t('event_create.startDate')}
              error={errors?.date?.startDate?.message}
            >
              <input
                type="date"
                min={todayISO}
                {...register('date.startDate', {
                  required: t('validation.required'),
                  validate: {
                    notPast: (v) =>
                      !v || v >= todayISO || t('validation.no_past_dates'),
                  },
                })}
                className="field-input"
              />
            </Field>
            <Field
              label={t('event_create.endDate')}
              error={errors?.date?.endDate?.message}
            >
              <input
                type="date"
                min={watch('date.startDate') || todayISO}
                {...register('date.endDate', {
                  required: t('validation.required'),
                  validate: {
                    afterStart: (end) => {
                      const start = watch('date.startDate');
                      if (!start || !end) return true;
                      const open = watch('date.openHours') || '00:00';
                      const close = watch('date.closeHours') || '23:59';
                      const startDT = combineLocal(start, open);
                      const endDT = combineLocal(end, close);
                      return (
                        (startDT && endDT && endDT > startDT) ||
                        t('validation.end_after_start')
                      );
                    },
                  },
                })}
                className="field-input"
              />
            </Field>
            <Field label={t('event_create.openHours')}>
              <TimePicker
                name="date.openHours"
                control={control}
                stepMinutes={5}
                maxTime={watch('date.closeHours')}
                maxExclusive
                rules={{ required: t('event_create.validation.required') }}
              />
            </Field>
            <Field label={t('event_create.closeHours')}>
              <TimePicker
                name="date.closeHours"
                control={control}
                stepMinutes={5}
                minTime={watch('date.openHours')}
                minExclusive
                rules={{ required: t('event_create.validation.required') }}
              />
            </Field>
          </div>
        ) : (
          <div className="space-y-2.5">
            {fields.map((field, index) => {
              const openPath = `date.days.${index}.openHours`;
              const closePath = `date.days.${index}.closeHours`;
              const rowOpen = watch(openPath);
              const rowClose = watch(closePath);
              const dtRaw = watch(`date.days.${index}.date`);
              const dt = dtRaw ? new Date(`${dtRaw}T00:00:00`) : null;
              const dom = dt ? dt.getDate() : '–';
              const month = dt
                ? dt
                    .toLocaleDateString('en-GB', { month: 'short' })
                    .toUpperCase()
                : '';

              return (
                <div
                  key={field.id}
                  className="grid grid-cols-12 gap-3 items-end bg-paper-2 border border-line rounded-xl p-2.5"
                >
                  <div className="col-span-2 md:col-span-1 flex items-center justify-center">
                    <div className="dom-chip">
                      <span className="text-[9px] tracking-widest opacity-80 leading-none">
                        {month || '—'}
                      </span>
                      <span className="text-[20px] font-extrabold leading-none mt-1">
                        {dom}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-10 md:col-span-4">
                    <span className="field-label block mb-1">
                      {t('event_create.startDate')}
                    </span>
                    <input
                      type="date"
                      min={getMinSegmentDate(index)}
                      {...register(`date.days.${index}.date`, {
                        required: t('validation.required'),
                        validate: {
                          notPast: (v) =>
                            !v ||
                            v >= todayISO ||
                            t('validation.no_past_dates'),
                          noDuplicate: (v) => {
                            const all = (watch('date.days') || [])
                              .map((d) => d?.date)
                              .filter(Boolean);
                            const count = all.filter((x) => x === v).length;
                            return (
                              count <= 1 || t('validation.duplicate_day')
                            );
                          },
                          notBeforePrevious: (v) => {
                            const min = getMinSegmentDate(index);
                            return (
                              !v ||
                              v >= min ||
                              t(
                                'event_create.validation.day_must_be_after_previous'
                              )
                            );
                          },
                        },
                      })}
                      className="field-input"
                    />
                    {errors?.date?.days?.[index]?.date && (
                      <p className="text-rose-600 text-[11px] mt-1">
                        {t('event_create.date_error')}
                      </p>
                    )}
                  </div>
                  <div className="col-span-5 md:col-span-3">
                    <span className="field-label block mb-1">
                      {t('event_create.openHours')}
                    </span>
                    <TimePicker
                      name={openPath}
                      control={control}
                      stepMinutes={5}
                      maxTime={rowClose}
                      maxExclusive
                      rules={{ required: t('event_create.validation.required') }}
                    />
                  </div>
                  <div className="col-span-5 md:col-span-3">
                    <span className="field-label block mb-1">
                      {t('event_create.closeHours')}
                    </span>
                    <TimePicker
                      name={closePath}
                      control={control}
                      stepMinutes={5}
                      minTime={rowOpen}
                      minExclusive
                      rules={{ required: t('event_create.validation.required') }}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="w-9 h-9 grid place-items-center rounded-full text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition"
                      aria-label="remove"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() =>
                append({ date: '', openHours: '', closeHours: '' })
              }
              className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-bold text-ink-strong bg-brand hover:bg-brand-hover transition"
            >
              <PlusIcon className="w-4 h-4" /> {t('event_create.add_day')}
            </button>
          </div>
        )}
      </FormCard>
      </HelpedBlock>

      {/* What fits this event */}
      <HelpedBlock sectionKey="categories">
      <FormCard
        title={t('event_create.what_fits', 'What fits this event')}
        action={
          <span className="text-[11px] font-semibold text-muted">
            {selectedCategories.length}{' '}
            {t('event_create.categories', 'Categories')} ·{' '}
            {selectedMicroCategories.length}{' '}
            {t('event_create.subcategories', 'subcategories')}
          </span>
        }
      >
        <p className="-mt-1 mb-4 text-[12.5px] text-gray-500">
          {t(
            'event_create.what_fits_lede',
            'Pick at least one category. Brands use these to filter for events that match what they sell.'
          )}
        </p>

        <input
          type="hidden"
          {...register('categoriesAllowed', {
            validate: (v) =>
              (Array.isArray(v) && v.length > 0) ||
              t('event_create.validation.required_macro_category'),
          })}
        />
        <CategorySelectorGrid
          noTitle
          selectedMacros={selectedCategories}
          setSelectedMacros={setSelectedCategories}
          selectedMicros={selectedMicroCategories.map((m) => m.id)}
          setSelectedMicros={(microsOrUpdater) => {
            const prevIds = selectedMicroCategories.map((m) => m.id);
            const newIds =
              typeof microsOrUpdater === 'function'
                ? microsOrUpdater(prevIds)
                : microsOrUpdater;
            const objs = newIds.map((id) => {
              const cat =
                availableMicroCategories.find((mc) => mc.id === id) || {};
              return {
                id,
                macroId: cat.macroId,
                icon: cat.icon,
                name: cat.name,
              };
            });
            setSelectedMicroCategories(objs);
          }}
        />
        {errors?.categoriesAllowed && (
          <p className="text-rose-600 text-[12px] mt-3">
            {errors.categoriesAllowed.message}
          </p>
        )}
      </FormCard>
      </HelpedBlock>

      {/* Description */}
      <HelpedBlock sectionKey="description">
      <FormCard title={t('event_create.description_card', 'Description')}>
        <Field
          label={
            <span className="flex items-center justify-between w-full">
              <span>
                {t('event_create.short_description')}{' '}
                <span className="text-muted font-normal normal-case tracking-normal">
                  —{' '}
                  {t(
                    'event_create.short_description_caption',
                    'shown on event cards'
                  )}
                </span>
              </span>
              <CharCounter value={shortDescValue} max={SHORT_DESC_MAX} />
            </span>
          }
          error={errors.shortDescription?.message}
        >
          <Textarea
            rows={2}
            maxLength={SHORT_DESC_MAX}
            {...register('shortDescription', { maxLength: SHORT_DESC_MAX })}
            className="field-input resize-none"
            placeholder={t('event_create.short_description_placeholder')}
          />
        </Field>

        <Field
          className="mt-5"
          label={
            <span className="flex items-center justify-between w-full">
              <span>
                {t('event_create.long_description')}{' '}
                <span className="text-muted font-normal normal-case tracking-normal">
                  —{' '}
                  {t(
                    'event_create.long_description_caption',
                    'shown on the event detail page'
                  )}
                </span>
              </span>
              <CharCounter value={longDescValue} max={LONG_DESC_MAX} />
            </span>
          }
          error={errors.longDescription?.message}
        >
          <Textarea
            rows={8}
            maxLength={LONG_DESC_MAX}
            {...register('longDescription', { maxLength: LONG_DESC_MAX })}
            className="field-input resize-y"
            placeholder={t('event_create.long_description_placeholder')}
          />
        </Field>
      </FormCard>
      </HelpedBlock>

      {/* Web presence */}
      <HelpedBlock sectionKey="webPresence">
      <FormCard
        title={t('event_create.web_presence', 'Web presence')}
        action={
          <span className="text-[11px] text-muted font-semibold">
            {t('common.optional', 'Optional')}
          </span>
        }
      >
        <Field
          label={t('event_create.website')}
          error={errors.website?.message}
        >
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
              <GlobeAltIcon className="w-4 h-4" />
            </span>
            <input
              {...register('website')}
              className="field-input pl-11"
              placeholder={t(
                'event_create.website_placeholder',
                'https://your-event.com'
              )}
            />
          </div>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {socialFields.map((s) => (
            <Field key={s.key} label={s.label}>
              <div className="flex items-stretch border border-line rounded-[10px] bg-white focus-within:border-brand focus-within:shadow-glow transition">
                <span className="px-3 grid place-items-center bg-paper-2 rounded-l-[10px] border-r border-line">
                  <img src={s.icon} alt={s.label} className="w-4 h-4" />
                </span>
                <span className="px-2 grid place-items-center text-[12px] text-muted bg-paper-2 border-r border-line whitespace-nowrap">
                  {s.prefix}
                </span>
                <input
                  {...register(s.key)}
                  className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none rounded-r-[10px] min-w-0"
                  placeholder="username"
                />
              </div>
            </Field>
          ))}
        </div>
      </FormCard>
      </HelpedBlock>
    </StepShell>
  );
};

export default Step1;
